import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, Typography } from "antd";
import { AudioOutlined, DeleteOutlined, StopOutlined } from "@ant-design/icons";

const { Text } = Typography;

function ProdBetaNudgeModal({
    eligible,
    open,
    username,
    onClose,
    onGoDev,
} = {}) {
    if (!eligible) return null;
    return (
        <Modal
            title="Quick help?"
            open={open}
            rootClassName="liquid-glass-modal"
            closable={true}
            maskClosable={false}
            keyboard={false}
            onCancel={onClose}
            footer={[
                <Button key="stay" onClick={onClose}>
                    Continue here
                </Button>,
                <Button
                    key="go_dev"
                    type="primary"
                    className="primary-glass-btn"
                    onClick={onGoDev}
                >
                    Switch to dev env
                </Button>,
            ]}
        >
            <div style={{ lineHeight: 1.5 }}>
                <p style={{ marginTop: 0 }}>
                    Hi <strong>{String(username || "").trim()}</strong>,
                </p>
                <p>
                    Sam could use your help testing the <strong>dev environment</strong>.
                    If you have a minute, try your next session on <strong>dev.simplyride.app</strong>.
                </p>
                <p style={{ marginBottom: 0 }}>
                    You can use the <strong>same login</strong>, and your <strong>session history is shared</strong> between environments - so no worries.
                </p>
            </div>
        </Modal>
    );
}

export const DEV_FEEDBACK_HOSTNAMES = [
    "dev.simplyride.app",
    // local dev
    //"localhost",
    // "127.0.0.1",
    //"::1",
];

export const PROD_BETA_NUDGE_HOSTNAMES = [
    "simplyride.app",
    // allow local prod-like testing
    //"localhost",
    //"127.0.0.1",
    //"::1",
];

// Edit this list to highlight risky/dev-only areas.
// If empty, the UI will fall back to generic messaging.
export const DEV_FEEDBACK_ATTENTION_ITEMS = ["ERG safety measures, pay attention to ERG power changes and play with power during sessions (if possible, adjust target power to see if it works)"];

const getHostname = () => {
    try {
        return window?.location?.hostname || "";
    } catch {
        return "";
    }
};

const isDevHost = () => {
    try {
        // Use hostname (no port), because users might run via :443 / :8443 etc.
        const hn = getHostname();
        return Boolean(hn && DEV_FEEDBACK_HOSTNAMES.includes(hn));
    } catch {
        return false;
    }
};

const isProdBetaNudgeHost = () => {
    try {
        const hn = getHostname();
        // Don't show the "go dev" prompt while already on dev.simplyride.app
        if (hn === "dev.simplyride.app") return false;
        return Boolean(hn && PROD_BETA_NUDGE_HOSTNAMES.includes(hn));
    } catch {
        return false;
    }
};

const buildAttentionNode = (items) => {
    const list = Array.isArray(items) ? items.filter(Boolean) : [];
    if (list.length === 0) return null;
    return (
        <div
            style={{
                padding: 12,
                borderRadius: 10,
                background: "rgba(255, 225, 0, 0.14)",
                border: "1px solid rgba(255, 200, 0, 0.35)",
                marginBottom: 12,
            }}
        >
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Pay extra attention to:</div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
                {list.map((it, idx) => (
                    <li key={`${idx}-${String(it).slice(0, 40)}`}>{it}</li>
                ))}
            </ul>
        </div>
    );
};

const buildDevFeedbackText = ({ prefill } = {}) => {
    const base = "[Dev feedback]\n";
    if (typeof prefill === "string" && prefill.trim()) {
        return `${base}${prefill.trim()}`;
    }
    return base;
};

const buildDevFeedbackPayloadText = ({ mode, userText } = {}) => {
    const clean = typeof userText === "string" ? userText.trim() : "";
    if (mode === "all_good") {
        const prefix = "[All good, nothing to report]";
        return `${buildDevFeedbackText({ prefill: prefix })}${clean ? `\n${clean}` : ""}`.trim();
    }
    // default: "message"
    return `${buildDevFeedbackText({ prefill: "" })}${clean ? clean : ""}`.trim();
};

/**
 * useDevFeedback
 * - Shows a welcome modal once per browser session (after user is loaded)
 * - Exposes a handler to show a post-save dialog (triggered after session save)
 * - Provides an after-save dev feedback modal that posts to the same backend API (/send_feedback/)
 */
export function useDevFeedback({
    userLoaded,
    username,
    isBetaTester,
    attentionItems,
    axiosInstance,
    alertDlg,
    suppress = false,
} = {}) {
    const isDev = useMemo(() => isDevHost(), []);
    const isProdNudgeHost = useMemo(() => isProdBetaNudgeHost(), []);
    const [welcomeOpen, setWelcomeOpen] = useState(false);
    const [afterSaveOpen, setAfterSaveOpen] = useState(false);
    const welcomeShownRef = useRef(false);
    const prodNudgeShownRef = useRef(false);
    const [prodNudgeOpen, setProdNudgeOpen] = useState(false);

    const attentionNode = useMemo(
        () => buildAttentionNode(attentionItems),
        [attentionItems]
    );

    const prodNudgeEligible = Boolean(
        isProdNudgeHost && userLoaded && isBetaTester && typeof username === "string" && username.trim()
    );

    // Show welcome modal once per *page load* (refresh will show again), only after user is loaded.
    // (We use a ref to avoid repeated opens across re-renders.)
    useEffect(() => {
        if (suppress) return;
        if (!isDev) return;
        // If we're also showing the beta-tester nudge, show the dev welcome AFTER the nudge is dismissed.
        if (prodNudgeEligible && prodNudgeOpen) return;
        if (!userLoaded) return;
        if (welcomeShownRef.current) return;
        welcomeShownRef.current = true;
        setWelcomeOpen(true);
    }, [isDev, userLoaded, prodNudgeEligible, prodNudgeOpen, suppress]);

    // Show the prod "go try dev" modal once per page load for beta testers
    useEffect(() => {
        if (suppress) return;
        if (!prodNudgeEligible) return;
        if (prodNudgeShownRef.current) return;
        prodNudgeShownRef.current = true;
        setProdNudgeOpen(true);
    }, [prodNudgeEligible, suppress]);

    // If onboarding tours are visible, immediately hide dev modals.
    useEffect(() => {
        if (!suppress) return;
        try { setWelcomeOpen(false); } catch { }
        try { setProdNudgeOpen(false); } catch { }
        try { setAfterSaveOpen(false); } catch { }
    }, [suppress]);

    const onSessionSavedUiConfirmed = useCallback(() => {
        if (!isDev) return;
        setAfterSaveOpen(true);
    }, [isDev]);

    const modals = useMemo(() => {
        const items = Array.isArray(attentionItems)
            ? attentionItems.filter(Boolean)
            : [];

        // eslint-disable-next-line react/no-unstable-nested-components
        const AfterSaveDevFeedbackModal = () => {
            const [text, setText] = useState("");
            const [audioBlob, setAudioBlob] = useState(null);
            const [isRecording, setIsRecording] = useState(false);
            const [recordingSeconds, setRecordingSeconds] = useState(0);
            const [isSending, setIsSending] = useState(false);
            const [errorMsg, setErrorMsg] = useState("");
            const mediaRecorderRef = useRef(null);
            const recordingTimerRef = useRef(null);

            const reset = () => {
                setText("");
                setAudioBlob(null);
                setIsRecording(false);
                setRecordingSeconds(0);
                setIsSending(false);
                setErrorMsg("");
                try {
                    if (recordingTimerRef.current) {
                        clearInterval(recordingTimerRef.current);
                        recordingTimerRef.current = null;
                    }
                } catch { }
                try {
                    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
                        mediaRecorderRef.current.stop();
                    }
                } catch { }
                mediaRecorderRef.current = null;
            };

            useEffect(() => {
                if (!afterSaveOpen) {
                    reset();
                }
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [afterSaveOpen]);

            const startRecording = async () => {
                setErrorMsg("");
                try {
                    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                    const mimeTypes = [
                        "audio/webm;codecs=opus",
                        "audio/webm",
                        "audio/ogg;codecs=opus",
                        "audio/ogg",
                        "audio/mp4",
                    ];
                    const supportedType = mimeTypes.find((t) =>
                        window.MediaRecorder && MediaRecorder.isTypeSupported ? MediaRecorder.isTypeSupported(t) : true
                    );
                    const mr = supportedType ? new MediaRecorder(stream, { mimeType: supportedType }) : new MediaRecorder(stream);
                    const chunks = [];
                    mr.ondataavailable = (e) => {
                        if (e.data && e.data.size > 0) chunks.push(e.data);
                    };
                    mr.onstop = () => {
                        const blobType =
                            supportedType && supportedType.includes("ogg")
                                ? "audio/ogg"
                                : supportedType && supportedType.includes("mp4")
                                    ? "audio/mp4"
                                    : "audio/webm";
                        const blob = new Blob(chunks, { type: blobType });
                        setAudioBlob(blob);
                        try {
                            stream.getTracks().forEach((t) => t.stop());
                        } catch { }
                    };
                    mediaRecorderRef.current = mr;
                    setRecordingSeconds(0);
                    setIsRecording(true);
                    mr.start(1000);
                    try {
                        recordingTimerRef.current && clearInterval(recordingTimerRef.current);
                    } catch { }
                    recordingTimerRef.current = setInterval(() => {
                        setRecordingSeconds((s) => s + 1);
                    }, 1000);
                } catch (e) {
                    setIsRecording(false);
                    setErrorMsg("Microphone permission denied or not available.");
                }
            };

            const stopRecording = () => {
                try {
                    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
                        mediaRecorderRef.current.stop();
                    }
                } catch { }
                try {
                    if (recordingTimerRef.current) {
                        clearInterval(recordingTimerRef.current);
                        recordingTimerRef.current = null;
                    }
                } catch { }
                setIsRecording(false);
            };

            const toggleRecording = () => {
                if (isRecording) stopRecording();
                else startRecording();
            };

            const resetRecording = () => {
                stopRecording();
                setAudioBlob(null);
                setRecordingSeconds(0);
            };

            const canSendMessage = Boolean((text || "").trim() || audioBlob);

            const send = async ({ mode }) => {
                if (!axiosInstance) {
                    setErrorMsg("Feedback service not available.");
                    return;
                }
                if (mode !== "all_good" && !canSendMessage) {
                    setErrorMsg("Please type a message or record an audio note.");
                    return;
                }
                setIsSending(true);
                setErrorMsg("");
                try {
                    const formData = new FormData();
                    const finalText = buildDevFeedbackPayloadText({ mode, userText: text });
                    if (finalText) formData.append("feedback", finalText);
                    if (audioBlob) {
                        const inferredExt =
                            audioBlob.type.includes("webm") ? "webm" :
                                audioBlob.type.includes("ogg") ? "ogg" :
                                    audioBlob.type.includes("mp3") || audioBlob.type.includes("mpeg") ? "mp3" :
                                        audioBlob.type.includes("wav") ? "wav" : "dat";
                        formData.append("audio", audioBlob, `audio_${Date.now()}.${inferredExt}`);
                        formData.append("audio_duration_sec", Math.min(Math.round(recordingSeconds), 600).toString());
                    }
                    await axiosInstance.post("/send_feedback/", formData, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });

                    // close + reset
                    setAfterSaveOpen(false);
                    reset();
                } catch (e) {
                    setErrorMsg("Error when submitting. Please try again.");
                    try {
                        alertDlg && alertDlg("Error when submitting. Please try again.");
                    } catch { }
                } finally {
                    setIsSending(false);
                }
            };

            return (
                <Modal
                    title="Dev feedback"
                    open={afterSaveOpen}
                    rootClassName="liquid-glass-modal"
                    closable={true}
                    maskClosable={false}
                    keyboard={false}
                    onCancel={() => setAfterSaveOpen(false)}
                    footer={[
                        <Button
                            key="all_good"
                            onClick={() => send({ mode: "all_good" })}
                            loading={isSending}
                            disabled={isRecording}
                        >
                            All good, nothing to report
                        </Button>,
                        <Button
                            key="send"
                            type="primary"
                            className="primary-glass-btn"
                            onClick={() => send({ mode: "message" })}
                            loading={isSending}
                            disabled={!canSendMessage || isRecording}
                        >
                            Send message
                        </Button>,
                    ]}
                >
                    <div style={{ lineHeight: 1.45 }}>
                        <p style={{ marginTop: 0, marginBottom: 10 }}>
                            This session has been saved and will be kept in both <strong>production and dev environments</strong> (shared history). No worries! But since this is a <strong>dev environment</strong>, if anything felt off, please tell us.
                        </p>
                        {items.length > 0 ? attentionNode : null}

                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                                <Button
                                    type={isRecording ? "primary" : "default"}
                                    danger={isRecording}
                                    shape="circle"
                                    onClick={toggleRecording}
                                    style={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                    disabled={isSending}
                                    aria-label={isRecording ? "Stop recording" : "Start recording"}
                                >
                                    {isRecording ? <StopOutlined style={{ fontSize: 22 }} /> : <AudioOutlined style={{ fontSize: 22 }} />}
                                </Button>
                                <div style={{ fontSize: 12, opacity: 0.8, textAlign: "center" }}>
                                    {isRecording ? (
                                        <>Recording… {String(Math.floor(recordingSeconds / 60)).padStart(2, "0")}:{String(recordingSeconds % 60).padStart(2, "0")}</>
                                    ) : audioBlob ? (
                                        "Recorded audio ready"
                                    ) : (
                                        "Tap mic to add an audio note"
                                    )}
                                </div>

                                {audioBlob ? (
                                    <div style={{ width: "100%" }}>
                                        <audio controls src={URL.createObjectURL(audioBlob)} style={{ width: "100%" }} />
                                        <div style={{ marginTop: 6, textAlign: "right" }}>
                                            <Button
                                                size="small"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={resetRecording}
                                                disabled={isSending || isRecording}
                                            >
                                                Delete
                                            </Button>
                                        </div>
                                    </div>
                                ) : null}
                            </div>

                            <div>
                                <Text strong>Your message</Text>
                                <div style={{ marginTop: 6 }}>
                                    <textarea
                                        value={text}
                                        onChange={(e) => setText(e.target.value)}
                                        placeholder="What went well? What didn’t? Steps to reproduce if needed…"
                                        rows={5}
                                        style={{
                                            width: "100%",
                                            resize: "vertical",
                                            padding: 10,
                                            borderRadius: 10,
                                            border: "1px solid rgba(0,0,0,0.15)",
                                            outline: "none",
                                            fontFamily: "inherit",
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        {errorMsg ? (
                            <div style={{ marginTop: 10, color: "rgba(200,0,0,0.95)", fontWeight: 700 }}>
                                {errorMsg}
                            </div>
                        ) : null}
                    </div>
                </Modal>
            );
        };

        return (
            <>
                <ProdBetaNudgeModal
                    eligible={prodNudgeEligible}
                    open={prodNudgeOpen}
                    username={username}
                    onClose={() => setProdNudgeOpen(false)}
                    onGoDev={() => {
                        try { setProdNudgeOpen(false); } catch { }
                        try {
                            window.open("https://dev.simplyride.app", "_blank", "noopener,noreferrer");
                        } catch {
                            // fallback: navigate current tab
                            try {
                                window.location.href = "https://dev.simplyride.app";
                            } catch { }
                        }
                    }}
                />

                <Modal
                    title="Dev environment"
                    open={welcomeOpen}
                    rootClassName="liquid-glass-modal"
                    onCancel={() => setWelcomeOpen(false)}
                    footer={[
                        <Button key="ok" type="primary" className="primary-glass-btn" onClick={() => setWelcomeOpen(false)}>
                            Got it
                        </Button>,
                    ]}
                >
                    <div style={{ lineHeight: 1.45 }}>
                        <p style={{ marginTop: 0 }}>
                            You’re on the <strong>dev environment</strong>.
                        </p>
                        {items.length > 0 ? (
                            <>
                                <p style={{ marginBottom: 10 }}>
                                    Please pay extra attention to the following:
                                </p>
                                {attentionNode}
                            </>
                        ) : (
                            <p>
                                Things can be a bit unstable here — please keep an eye out for any weird behavior.
                            </p>
                        )}
                        <p style={{ marginBottom: 0 }}>
                            Don’t hesitate to send feedback if something goes wrong.
                        </p>
                    </div>
                </Modal>

                <AfterSaveDevFeedbackModal />
            </>
        );
    }, [welcomeOpen, afterSaveOpen, attentionItems, attentionNode, axiosInstance, alertDlg, prodNudgeEligible, prodNudgeOpen, username]);

    return {
        isDev,
        modals,
        onSessionSavedUiConfirmed,
    };
}


